import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { imageCacheService } from '../services/imageCacheService';
import { isExternalProfileImageSource } from '../utils/memberProfileDisplay';

interface CachedProfileImageProps {
  imageId: string;
  alt?: string;
  className?: string;
  onError?: () => void;
  onLoad?: () => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  fallbackImageId?: string;
}

const CachedProfileImage: React.FC<CachedProfileImageProps> = ({
  imageId,
  alt = "프로필",
  className,
  onError,
  onLoad,
  onDoubleClick,
  fallbackImageId = 'basic-0'
}) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        // 외부 URL(사진 프로필)은 즉시 src에 연결해 브라우저 캐시를 우선 활용
        if (isExternalProfileImageSource(imageId)) {
          if (isMounted) {
            setImageUrl(imageId);
            setIsLoading(false);
            onLoad?.();
          }
          return;
        }

        const url = await imageCacheService.getImage(imageId);
        
        if (isMounted) {
          setImageUrl(url);
          setIsLoading(false);
          onLoad?.();
        }
      } catch (error) {
        console.error('프로필 이미지 로드 실패:', error);
        
        if (isMounted) {
          setHasError(true);
          setIsLoading(false);
          onError?.();
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [imageId, onError, onLoad]);

  // 에러 발생 시 폴백 이미지 로드
  useEffect(() => {
    if (hasError && fallbackImageId !== imageId) {
      let isMounted = true;

      const loadFallback = async () => {
        try {
          const fallbackUrl = await imageCacheService.getImage(fallbackImageId);
          
          if (isMounted) {
            setImageUrl(fallbackUrl);
            setHasError(false);
          }
        } catch (error) {
          console.error('폴백 이미지 로드 실패:', error);
        }
      };

      loadFallback();

      return () => {
        isMounted = false;
      };
    }
  }, [hasError, fallbackImageId, imageId]);

  if (isLoading) {
    return (
      <ImageContainer className={className}>
        <LoadingSpinner>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="transparent" strokeWidth="2"/>
            <path d="M12 2C14.2 2 16.2 2.8 17.8 4.2" stroke="var(--color-text-gray)" strokeWidth="2" strokeLinecap="round">
              <animateTransform
                attributeName="transform"
                type="rotate"
                dur="1s"
                repeatCount="indefinite"
                values="0 12 12;360 12 12"
              />
            </path>
          </svg>
        </LoadingSpinner>
      </ImageContainer>
    );
  }

  if (hasError && !imageUrl) {
    return (
      <ImageContainer className={className}>
        <ErrorIcon>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="transparent" strokeWidth="2"/>
            <path d="M12 8V12M12 16H12.01" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </ErrorIcon>
      </ImageContainer>
    );
  }

  return (
    <StyledImage
      src={imageUrl}
      alt={alt}
      className={className}
      onError={() => {
        if (!hasError) {
          setHasError(true);
          onError?.();
        }
      }}
      onLoad={() => {
        setIsLoading(false);
        onLoad?.();
      }}
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDoubleClick?.(e);
      }}
    />
  );
};

export default CachedProfileImage;

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  max-width: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 6px;
  background: transparent;
  display: block;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-gray);
`;

const ErrorIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
`;
